import { FC, useState, useMemo, useEffect, memo } from 'react'
import type { ProductListRes, ProductFiltersRes, ProductListReq } from '@/types/api'
import Filter from '@/pages/library/filter'
import useLocale from '@/utils/useLocale'
import EchoCard from '@/components/EchoCard'
import { Space, Tooltip, Skeleton, Switch, Button } from '@arco-design/web-react'
import { IconPlus } from '@arco-design/web-react/icon'
import { getParamsFromQueryForProduct, useUrlSyncParams, initParams } from '../hooks/useUrlSyncParams'
import IconSearch from '@/assets/ic-search.png'
import Link from 'next/link'
import Image from 'next/image'
import CollectButton from '@/components/CollectButton'
import { starProductApi, unStarProductApi } from '@/api/favorites'
import generateExportUrl from '@/utils/generateExportUrl'
import styles from '../style/index.module.less'
import { renderMaskValue, MaskValue } from '@/components/Mask'
import { getProductListApi, getProductTrendsListApi } from '@/api/product'
import { CategoriesItem } from '@/types/api'
import { SEOHeader } from '@/components/SEOHeader'
import { RenderProductListColumn, ProductTrendTitle } from '../custom-render'
import useTable from '../hooks/useTable'
import Search from '@/components/Search'
import { SearchTypes } from '@/types/common'
import { useRouter } from 'next/router'
import { useSelector, useDispatch } from 'react-redux'
import { StoreState, updateProductNameTranslate } from '@/store'
import useRegionOptions from '@/hooks/useRegionOptions'
import { ModuleCode } from '@/utils/moduleCode'
import { useMount, useLocalStorageState } from 'ahooks'
import { isEmpty } from '@/utils/is'
import { useProductsFilterLocalStorage, useThreeLevelCategoriesLocalStorage } from '@/hooks/useSWRApi'
import { RelationShop } from '@/pages/platform/leaderboard/products/leaderboard'
import { DATE_RANGE_PRODUCT } from '../hooks/useUrlSyncParams'
import CustomField from '@/components/CustomTableField'
import useCustomField from '@/hooks/useCustomField'
import { PRODUCT_NAME_TRANSLATE_STORAGE_KEY } from '@/utils/constants'
import { useIsZh } from '@/hooks/useIsZh'
import ManualRecordModal from '@/components/ManualRecordModal'
import { TableSecondaryCurrency } from '@/components/business/table/secondary-currency'

type Props = {
  filtersFromSSR: ProductFiltersRes
  paramsFromSSR: ProductListReq
  ssrCtx: any
}

export const ManualRecordButton: FC<{ type: 'product' | 'shop' }> = ({ type }) => {
  const t = useLocale()
  const [manualRecordVisible, setManualRecordVisible] = useState(false)
  return (
    <>
      <Button
        type="primary"
        size="large"
        onClick={() => setManualRecordVisible(true)}
        className={styles.manualRecordBtn}
        icon={<IconPlus />}
      >
        {t['manual_record']}
      </Button>
      <ManualRecordModal
        visible={manualRecordVisible}
        onCancel={() => setManualRecordVisible(false)}
        successCallback={(data) => {
          console.log(data)
        }}
        type={type}
      />
    </>
  )
}

export const useEnumDate = () => {
  const t = useLocale()

  return {
    '1': t['product.column.sales_yesterday'],
    '7': t['product.column.sales_day7'],
    '30': t['product.column.sales_day30'],
    '90': t['product.column.sales_day90']
  }
}

export const useEnumDateGmv = () => {
  const t = useLocale()

  return {
    '1': t['product.column.gmv_yesterday'],
    '7': t['product.column.gmv_day7'],
    '30': t['product.column.gmv_day30'],
    '90': t['product.column.gmv_day90']
  }
}

export const useDateList = () => {
  const t = useLocale()
  return [
    {
      label: t['product.filter.yesterday'],
      value: '1'
    },
    {
      label: t['product.filter.last_week'],
      value: '7'
    },
    {
      label: t['product.filter.last_month'],
      value: '30'
    }
  ]
}

export const ProductNameTranslateToggle = () => {
  const isZh = useIsZh()
  const t = useLocale()
  const dispatch = useDispatch()
  const productNameTranslate = useSelector((state: StoreState) => state.global.productNameTranslate)
  const [isTranslate, setIsTranslate] = useLocalStorageState(PRODUCT_NAME_TRANSLATE_STORAGE_KEY, {
    defaultValue: false
  })

  // 初始化时同步localStorage的值到Redux (只在组件挂载时执行一次)
  useEffect(() => {
    if (isTranslate !== productNameTranslate) {
      dispatch(updateProductNameTranslate(isTranslate))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTranslate = () => {
    const newTranslateState = !isTranslate
    setIsTranslate(newTranslateState)
    // 同步更新Redux状态
    dispatch(updateProductNameTranslate(newTranslateState))
  }

  return (
    <Space>
      <span>{isZh ? '一键翻译' : t['product.column.productName']}</span>
      {isZh ? (
        <div className="flex items-center">
          <Tooltip content={t['product_name_translate_english']}>
            <Switch checked={isTranslate} onChange={handleTranslate} size="small" />
          </Tooltip>
        </div>
      ) : null}
    </Space>
  )
}

const useColumns = (dateRangeParam?: string) => {
  const t = useLocale()
  const router = useRouter()
  const dateRange = dateRangeParam || router.query.dateRange

  const ENUM_DATE = useEnumDate()
  const ENUM_DATE_GMV = useEnumDateGmv()

  const list = useMemo(() => {
    return [
      {
        title: t['product.column.productName'],
        dataIndex: 'product_name',
        render(_: any, record: ProductListRes[0]) {
          return <RenderProductListColumn record={record} />
        },
        width: 360,
        headerCellStyle: {
          padding: '5px 0'
        }
      },
      {
        title: t['relation_shop'],
        dataIndex: 'seller',
        width: 210,
        render: (_: any, record: ProductListRes[0]) => {
          return <RelationShop record={record.seller} />
        },
        align: 'center'
      },
      {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        title: ENUM_DATE[dateRange] || ENUM_DATE[DATE_RANGE_PRODUCT],
        dataIndex: 'total_sale_nd_cnt',
        render: (val: string) => {
          return <MaskValue value={val} style={{ color: '#EB50AA' }} />
        },
        width: 90,
        defaultSortOrder: 'descend',
        openSort: true,
        align: 'center'
      },
      {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        title: ENUM_DATE_GMV[dateRange] || ENUM_DATE_GMV[DATE_RANGE_PRODUCT],
        dataIndex: 'total_sale_gmv_nd_amt',
        render: (_: string, record: any) => {
          return <TableSecondaryCurrency record={record} filedName="total_sale_gmv_nd_amt" />
        },
        align: 'center',
        defaultSortOrder: 'descend',
        width: 100,
        openSort: true
      },
      {
        title: <ProductTrendTitle />,
        dataIndex: 'sales_trending',
        width: 140,
        align: 'center'
      },
      {
        title: t['influencer.detail.basic.contents.trend.sales.total'],
        dataIndex: 'total_sale_cnt',
        render: renderMaskValue,
        align: 'center',
        openSort: true
      },

      {
        title: t['workplace.GMV'],
        dataIndex: 'total_sale_gmv_amt',
        render: (_: string, record: any) => {
          return <TableSecondaryCurrency record={record} filedName="total_sale_gmv_amt" />
        },
        align: 'center',
        openSort: true
      },
      {
        title: t['product.column.influencersCount'],
        dataIndex: 'influencers_count',
        render: renderMaskValue,
        align: 'center',
        openSort: true
      },
      {
        title: t['influencer.detail.Related.video_count'],
        dataIndex: 'videos_count',
        render: renderMaskValue,
        align: 'center',
        openSort: true
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, t])

  return list
}

const useProductDateList = () => {
  const t = useLocale()
  return [
    {
      label: t['product.filter.yesterday_sales'],
      value: '1'
    },
    {
      label: t['product.filter.last_week_sales'],
      value: '7'
    },
    {
      label: t['product.filter.last_month_sales'],
      value: '30'
    },
    {
      label: t['product.filter.last90_sales'],
      value: '90'
    }
  ]
}

const LOCAL_ID = 'products_columns'
const Products: FC<Props> = memo((props) => {
  const t = useLocale()
  const { paramsFromSSR, ssrCtx, filtersFromSSR } = props
  const { currency } = useSelector((state: StoreState) => state.ssr)
  const { searchType } = useSelector((state: StoreState) => state.global)

  const { regionOptions } = useRegionOptions()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const activeRegion = regionOptions.find((item) => item.value === currency)!
  const router = useRouter()
  const [currentSEOCategories, setCurrentSEOCategories] = useState<any>({})
  const [currentKeyword, setCurrentKeyword] = useState('')
  const { filtersFromSSR: threeLevelCategoriesFromCSR, isLoading: threeLevelCategoriesLoading } =
    useThreeLevelCategoriesLocalStorage({ ssrCtx })
  const columnList = useColumns()
  const dateList = useProductDateList()
  const [resetFlag, setResetFlag] = useState<number | null>(null)
  const { defaultColumns, customFields, setCustomFields } = useCustomField({
    id: LOCAL_ID,
    rawColumns: columnList,
    disabledKeys: ['product_name', 'seller']
  })

  const pinColumns = [
    {
      title: <ProductNameTranslateToggle />,
      dataIndex: 'product_name',
      render(_: any, record: ProductListRes[0]) {
        return <RenderProductListColumn record={record} />
      },
      width: 360,
      fixed: 'left',
      headerCellStyle: {
        padding: '5px 0'
      }
    },
    {
      title: t['relation_shop'],
      dataIndex: 'seller',
      width: 210,
      render: (_: any, record: ProductListRes[0]) => {
        return <RelationShop record={record.seller} />
      },
      align: 'center'
    }
  ]

  const finalColumns: any[] = [
    ...pinColumns,
    ...customFields.map((item) => ({ ...item, align: 'center' })),
    {
      title: t['common.list.action'],
      dataIndex: '',
      align: 'center',
      width: 100,
      fixed: 'right',
      render(_: any, record: ProductListRes[0]) {
        return (
          <Space>
            <Link href={`/products/${record.product_id}`}>
              <a target="_blank">
                <div style={{ width: 32, height: 32 }}>
                  <Image src={IconSearch.src} width={32} height={32} />
                </div>
              </a>
            </Link>
            <CollectButton
              type="products"
              id={record.product_id}
              collectStatus={record.collect_status.id}
              onCollect={() => starProductApi(record.product_id)}
              onUnCollect={() => unStarProductApi(record.product_id)}
            />
          </Space>
        )
      }
    }
  ]
  const handleExport = (key?: string) => {
    const commonParams = { ...params, type: searchType }
    const query = key ? { ...commonParams, export_count: key } : commonParams
    window.open(
      generateExportUrl({
        url: `/data/products/list/export`,
        query: query
      })
    )
  }

  const sortList = [
    {
      title: t['shops.avg_price'],
      dataIndex: 'avg_price',
      openSort: true
    },
    ...columnList
  ].filter((column) => column.openSort)

  const { renderTable, params, setParams, table } = useTable({
    getTableApi: getProductListApi,
    columns: finalColumns,
    paramsFromSSR,
    tableFromSSR: {
      isLoading: false,
      list: [],
      page: {
        current: 1,
        total: 0,
        pageSize: 10
      }
    },
    rowKey: 'product_id',
    onExport: handleExport,
    title: t['product.column.product_list'],
    sortList: sortList,
    moduleCode: ModuleCode['library.filters'],
    getChartsApi: getProductTrendsListApi,
    chartDataIndex: 'sales_trending',
    exportId: 'export.products',
    customField: (
      <CustomField
        id={LOCAL_ID}
        onOk={(list) => {
          const newList = list
            .map((l) => {
              return {
                dataIndex: l.dataIndex,
                isSelected: l.isSelected,
                title: columnList.find((i) => i.dataIndex === l.dataIndex)?.title
              }
            })
            .filter((item) => item.isSelected === true)
          setCustomFields(newList)
        }}
        defaultFields={defaultColumns}
      />
    )
  })

  useUrlSyncParams(params)

  const renderSEOTitle = () => {
    return `${t['seo.top']} ${currentSEOCategories?.label || ''} ${t['seo.tiktok_product']}`
  }

  const handleChangeSEOTitle = (nextParams: { [key: string]: any }) => {
    const productCategories = nextParams.product_categories
    if (productCategories !== undefined && Array.isArray(productCategories)) {
      const curId = productCategories[0]
      const curObj = threeLevelCategoriesFromCSR?.find((item: CategoriesItem) => item.value === curId)
      setCurrentSEOCategories(curObj)
    }
  }

  useMount(() => {
    const { product_categories } = router.query
    if (product_categories) {
      handleChangeSEOTitle({ product_categories })
    }
  })

  const handleSearch = (val: string) => {
    const obj = initParams(params)
    setParams({ ...obj, keyword: val, page: '1', type: searchType })
  }

  useEffect(() => {
    const keyWord = router.query.keyword as string
    if (keyWord && router.query.isGlobalSearch) {
      handleSearch(keyWord)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.keyword])

  return (
    <>
      <SEOHeader
        title={renderSEOTitle()}
        description={t['product.seo.meta.description'].replace('{category}', currentSEOCategories?.label || '')}
        keywords={t['seo.products.library.keywords']}
      />
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div className={styles.header}>
          <h1 className={styles.title} style={{ minWidth: 100 }}>
            {t['product.name']}
          </h1>
          <Space>
            <Search
              theme="light"
              defaultType={SearchTypes.Product}
              defaultValue={paramsFromSSR.keyword}
              onSearch={(value) => {
                handleSearch(value)
              }}
              setCurrentKeyword={(keyword: string) => setCurrentKeyword(keyword)}
              autoJumpWhenTypeChange
              resetFlag={resetFlag}
              searchByImage={true}
              imageSearchLoading={table?.isLoading}
              onImageSearch={(resData, keyword) => {
                const obj = initParams(params)
                setParams({ ...obj, image: resData, page: '1', keyword } as typeof params)
              }}
              onImageSearchClose={() => {
                //参数重置
                setParams({ ...params, image: '', keyword: '' } as typeof params)
              }}
            />
            <ManualRecordButton type="product" />
          </Space>
          {/* {isUSA ? (
            <Space>
              <Search
                theme="light"
                defaultType={SearchTypes.Product}
                defaultValue={paramsFromSSR.keyword}
                onSearch={(value) => {
                  handleSearch(value)
                }}
                setCurrentKeyword={(keyword: string) => setCurrentKeyword(keyword)}
                autoJumpWhenTypeChange
                resetFlag={resetFlag}
                searchByImage={true}
                imageSearchLoading={table?.isLoading}
                onImageSearch={(resData, keyword) => {
                  const obj = initParams(params)
                  setParams({ ...obj, image: resData, page: '1', keyword } as typeof params)
                }}
                onImageSearchClose={() => {
                  //参数重置
                  setParams({ ...params, image: '', keyword: '' } as typeof params)
                }}
              />
              <ManualRecordButton type="product" />
            </Space>
          ) : (
            <Search
              theme="light"
              defaultType={SearchTypes.Product}
              defaultValue={paramsFromSSR.keyword}
              onSearch={(value) => {
                handleSearch(value)
              }}
              setCurrentKeyword={(keyword: string) => setCurrentKeyword(keyword)}
              autoJumpWhenTypeChange
              resetFlag={resetFlag}
              searchByImage={true}
              imageSearchLoading={table?.isLoading}
              onImageSearch={(resData, keyword) => {
                const obj = initParams(params)
                setParams({ ...obj, image: resData, page: '1', keyword } as typeof params)
              }}
              onImageSearchClose={() => {
                //参数重置
                setParams({ ...params, image: '', keyword: '' } as typeof params)
              }}
            />
          )} */}
        </div>
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <Filter
            showCustomFilter
            id="products"
            loading={threeLevelCategoriesLoading}
            categoryFilters={[
              {
                label: t['filter.productCategories'],
                key: 'product_categories',
                categories: filtersFromSSR?.product_categories,
                threeLevelList: threeLevelCategoriesFromCSR,
                resultLabel: t['filter.productCategories']
              }
            ]}
            basicFilters={[
              {
                key: 'price',
                label: t['product.column.price'],
                options: filtersFromSSR?.more_filters?.price,
                type: 'selectInput'
              },
              {
                label: t['video.detail.comments'],
                key: 'review_count',
                type: 'selectInput',
                options: filtersFromSSR?.more_filters?.review_count
              },
              {
                label: t['product.detail.rating'],
                key: 'product_rating',
                type: 'selectInput',
                options: filtersFromSSR?.more_filters?.product_rating
              },
              {
                key: 'commission_rate',
                options: filtersFromSSR?.more_filters?.commission_rate,
                type: 'selectInput',
                label: t['product.column.commission_rate']
              },
              {
                label: t['product.column.influencersCount'],
                key: 'related_influencers',
                options: filtersFromSSR?.more_filters?.related_influencers,
                type: 'selectInput'
              },
              {
                label: t['influencer.detail.Related.video_count'],
                key: 'videos_count',
                options: filtersFromSSR?.more_filters?.videos_count,
                type: 'selectInput'
              },
              {
                label: t['videos_views_count'],
                key: 'views_count',
                type: 'selectInput',
                options: filtersFromSSR?.more_filters?.views_count
              },
              
              
            ]}
            advancedFilters={[
              {
                key: 'seller_type',
                options: [
                  {
                    id: '1',
                    key: '1',
                    name: t['shops.local']
                  },
                  {
                    id: '2',
                    key: '2',
                    name: t['shops.cross_border']
                  }
                ],
                type: 'select',
                label: t['Delivery_Type']
              },
              {
                key: 'is_s_shop',
                options: [
                  {
                    id: '1',
                    key: '1',
                    name: t['filter.is_yes']
                  },
                  {
                    id: '0',
                    key: '0',
                    name: t['filter.is_no']
                  }
                ],
                type: 'select',
                label: t['shops.filter.is_s_shop_label']
              },
              {
                key: 'shop_type',
                options: [
                  {
                    id: '1',
                    key: '1',
                    name: t['filter.is_yes']
                  },
                  {
                    id: '0',
                    key: '0',
                    name: t['filter.is_no']
                  }
                ],
                type: 'select',
                label: t['is_brand_shop_label']
              },
              {
                key: 'is_free_shipping',
                options: [
                  {
                    id: '1',
                    key: '1',
                    name: t['filter.is_yes']
                  },
                  {
                    id: '0',
                    key: '0',
                    name: t['filter.is_no']
                  }
                ],
                type: 'select',
                label: t['shops.filter.is_free_shipping']
              },
              {
                type: 'select',
                key: 'sales_flag',
                options: [
                  {
                    id: '2',
                    key: '2',
                    name: t['product.filter.live_selling']
                  },
                  {
                    id: '1',
                    key: '1',
                    name: t['product.filter.video_selling']
                  }
                ],
                label: t['shop.detail.main_methods']
              },
              {
                key: 'sales_trend_flag',
                options: [
                  {
                    id: '1',
                    key: '1',
                    name: t['product.filter.trend_up']
                  },
                  {
                    id: '2',
                    key: '2',
                    name: t['product.filter.trend_down']
                  }
                ],
                type: 'select',
                label: t['product.filter.sales_trend']
              },
              
             
              {
                label: t['table.Sales'],
                key: 'sales,total_sale_cnt',
                options: [],
                type: 'multipleSelectInput',
                optionMap: {
                  sales: {
                    data: filtersFromSSR.more_filters.sales,
                    name: t['sales_day30']
                  },
                  total_sale_cnt: {
                    data: filtersFromSSR.more_filters.total_sold_count,
                    name: t['influencer.detail.basic.contents.trend.sales.total']
                  }
                }
              },
              {
                label: t['gmv'],
                key: 'gmv,gmv_amt_30d',
                options: [],
                type: 'multipleSelectInput',
                optionMap: {
                  gmv: {
                    data: filtersFromSSR.more_filters.gmv,
                    name: t['total.GMV']
                  },
                  gmv_amt_30d: {
                    data: filtersFromSSR.more_filters.gmv_amt_30d,
                    name: t['GMV30']
                  }
                }
              },
              {
                key: 'dateRange',
                options: dateList.map((item) => ({
                  id: item.value,
                  key: item.value,
                  name: item.label
                })),
                type: 'select',
                tooltipText: t['product.column.date_tips'],
                label: t['product.filter.time_range']
              },
              {
                label: t['estimated_release_time'],
                key: 'start_time,end_time',
                type: 'dateRangePicker',
                tooltipText: t['estimated_release_time'],
                options: []
              }
            ]}
            params={params}
            onChange={(nextParams) => {
              if (nextParams['keyword'] === '') {
                setParams({ ...nextParams })
                setCurrentKeyword('')
                setResetFlag(new Date().getTime())
              } else {
                // 因为keyword不会实时更新，所以filter改变时，获取一次最新的keyword值
                setParams({ ...nextParams, keyword: currentKeyword })
              }
              handleChangeSEOTitle(nextParams)
            }}
          />
          <EchoCard>{renderTable()}</EchoCard>
        </Space>
      </Space>
    </>
  )
})

export const LibrarySkeleton = ({ loading }: { loading?: boolean }) => {
  return <Skeleton loading={loading} text={{ width: '90%', rows: 15 }} image={{ shape: 'circle' }} animation></Skeleton>
}

interface TProductsWrapper {
  ssrCtx: any
  queryFromSSR: Record<string, any>
}

const ProductsWrapper: FC<TProductsWrapper> = ({ ssrCtx, queryFromSSR }) => {
  const { filtersFromSSR } = useProductsFilterLocalStorage({ ssrCtx })

  if (isEmpty(filtersFromSSR)) {
    return <LibrarySkeleton loading={true} />
  }
  const paramsFromSSR = getParamsFromQueryForProduct(filtersFromSSR, queryFromSSR)

  return isEmpty(paramsFromSSR) ? null : (
    <Products paramsFromSSR={paramsFromSSR} ssrCtx={ssrCtx} filtersFromSSR={filtersFromSSR} />
  )
}

export default ProductsWrapper
